"use client";

import classNames from "classnames/bind";
import styles from "./HomepageHero.module.css";
import { useRef, useMemo } from "react";

import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";

import RivePlayer from "../RivePlayer/RivePlayer";
import { Button } from "../Button/Button";
import Image from "next/image";
import { getImage } from "@/helpers/imageHelper";
import type { Media } from "@/payload-types";

gsap.registerPlugin(ScrollTrigger);

const cx = classNames.bind(styles);

type RivePlayerRef = {
  play: () => void;
  pause: () => void;
  restart: () => void;
};

type HomepageHeroProps = {
  introText: string;
  section1Image: {
    file: Media | string;
    alt?: string;
  };
  section4_title: string;
  section4_btn1_label: string;
  section4_btn1_url: string;
  section4_btn2_label: string;
  section4_btn2_url: string;
  section4Image: {
    file: Media | string;
    alt?: string;
  };
  section4Rive?: {
    file: Media | string;
    animation: string;
  };
  section4MobileRive?: {
    file: Media | string;
    animation: string;
  };
  section4Graphic: {
    file: Media | string;
    alt?: string;
  };
};

// Helper function to get Rive file URL
const getRiveFileUrl = (riveFile: Media | string | undefined): string => {
  if (!riveFile) return "./rive/Home_Hero_Message_UI.riv";
  return typeof riveFile === "string"
    ? riveFile
    : riveFile.url || "./rive/Home_Hero_Message_UI.riv";
};

export function HomepageHero({
  introText,
  section1Image,
  section4_title,
  section4_btn1_label,
  section4_btn1_url,
  section4_btn2_label,
  section4_btn2_url,
  section4Image,
  section4Rive,
  section4MobileRive,
  section4Graphic,
}: HomepageHeroProps) {
  const rows = 10;
  const cols = 20;

  const containerRef = useRef<HTMLDivElement>(null);
  // Section #1
  const section1Ref = useRef<HTMLDivElement>(null);
  const imgSection1Ref = useRef<HTMLDivElement>(null);
  const title1Ref = useRef<HTMLHeadingElement>(null);

  // Section 4
  const section4Ref = useRef<HTMLDivElement>(null);
  const section4GraphicRef = useRef<HTMLDivElement>(null);
  const section4ContentRef = useRef<HTMLDivElement>(null);
  const section4TitleRef = useRef<HTMLHeadingElement>(null);
  const section4ButtonsRef = useRef<HTMLDivElement>(null);

  // Pixelated grid
  const cellRefs = useRef<HTMLDivElement[]>([]);
  const blackCellRefs = useRef<HTMLDivElement[]>([]);

  // Section 4 Rive refs
  const riveSection4Ref = useRef<RivePlayerRef>(null);
  const riveMobileSection4Ref = useRef<RivePlayerRef>(null);

  // Get all images with safety checks
  const heroImage1 = getImage(section1Image, section1Image?.alt, "hero");
  const heroImage4 = getImage(section4Image, section4Image?.alt, "hero");
  const heroGraphic = getImage(
    section4Graphic,
    section4Graphic?.alt,
    "graphic",
  );

  const gridCells = useMemo(() => {
    cellRefs.current = []; // Reset before rendering
    return Array.from({ length: rows * cols }, (_, i) => (
      <div
        key={i}
        className={cx("section1__gridOverlayColor")}
        ref={(el) => {
          if (el) {
            cellRefs.current[i] = el;
          }
        }}
      />
    ));
  }, []);

  const gridCellsBlack = useMemo(() => {
    blackCellRefs.current = []; // Reset before rendering
    return Array.from({ length: rows * cols }, (_, i) => (
      <div
        key={i}
        className={cx("section1__gridOverlayBlack")}
        ref={(el) => {
          if (el) {
            blackCellRefs.current[i] = el;
          }
        }}
      />
    ));
  }, []);

  const gridStyle = {
    gridTemplateColumns: `repeat(${cols}, 1fr)`,
    gridTemplateRows: `repeat(${rows}, 1fr)`,
  };

  useGSAP(() => {
    // Early return if critical refs are not available
    if (!containerRef.current || !section1Ref.current || !section4Ref.current) {
      return;
    }

    // Helper function to create word-by-word animation
    const createWordAnimation = (
      element: HTMLElement | null,
      delay: number = 0.5,
      staggerDelay: number = 0.15,
    ) => {
      if (!element) return 0;

      const text = element.textContent || "";
      const words = text.trim().split(" ");

      // Clear the original text and create word spans
      element.innerHTML = words
        .map(
          (word, index) =>
            `<span class="word-${index}" style="display: inline-block; opacity: 0; transform: translateX(20px);">${word}</span>`,
        )
        .join(" ");

      // Animate each word with stagger
      words.forEach((_, index) => {
        const wordElement = element.querySelector(`.word-${index}`);
        if (wordElement) {
          gsap.to(wordElement, {
            opacity: 1,
            x: 0,
            duration: 0.8,
            delay: delay + index * staggerDelay,
            ease: "power2.out",
            force3D: true,
          });
        }
      });

      // Return total animation duration
      return delay + words.length * staggerDelay + 0.8;
    };

    // Safari-specific fixes for text rendering
    if (title1Ref.current) {
      gsap.set(title1Ref.current, {
        // Prevent text distortion in Safari
        "-webkit-transform-style": "preserve-3d",
        "-webkit-backface-visibility": "hidden",
        "-webkit-perspective": 1000,
        "-webkit-font-smoothing": "antialiased",
        "text-rendering": "optimizeSpeed",
      });
    }

    // Apply word-by-word animation to title
    const titleAnimationDuration = createWordAnimation(
      title1Ref.current,
      0.5,
      0.15,
    );

    // Start the ripple effect after title animation completes
    setTimeout(() => {
      const runRipple = (startRow: number, startCol: number, baseDelay = 0) => {
        const visited = new Set<number>();
        const queue: { row: number; col: number; delay: number }[] = [];
        const getIndex = (r: number, c: number) => r * cols + c;

        queue.push({ row: startRow, col: startCol, delay: 0 });
        visited.add(getIndex(startRow, startCol));

        const directions = [
          [0, 1],
          [1, 0],
          [-1, 0],
          [0, -1],
        ];

        while (queue.length > 0) {
          const { row, col, delay } = queue.shift()!;
          const index = getIndex(row, col);

          const blackCell = blackCellRefs.current[index];
          const blurCell = cellRefs.current[index];

          if (blackCell) {
            gsap.to(blackCell, {
              opacity: 0,
              duration: 0.2,
              delay: baseDelay + delay * 0.064,
              ease: "power2.out",
            });
          }

          if (blurCell) {
            gsap.to(blurCell, {
              opacity: 0,
              duration: 0.2,
              delay: baseDelay + delay * 0.16,
              ease: "power2.out",
            });
          }

          for (const [dr, dc] of directions) {
            const newRow = row + dr;
            const newCol = col + dc;
            const newIndex = getIndex(newRow, newCol);

            if (
              newRow >= 0 &&
              newRow < rows &&
              newCol >= 0 &&
              newCol < cols &&
              !visited.has(newIndex)
            ) {
              visited.add(newIndex);
              queue.push({ row: newRow, col: newCol, delay: delay + 1 });
            }
          }
        }
      };

      // First ripple
      const startRow1 = Math.floor(Math.random() * rows);
      const startCol1 = Math.floor(Math.random() * cols);
      runRipple(startRow1, startCol1, 0);

      // Second ripple
      const startRow2 = Math.floor(Math.random() * rows);
      const startCol2 = Math.floor(Math.random() * cols);
      runRipple(startRow2, startCol2, 0);
    }, titleAnimationDuration * 1000);

    // Sections - only use section 1 and 4
    const activeSections = [section1Ref.current, section4Ref.current].filter(
      Boolean,
    );
    gsap.set(activeSections, {
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
    });

    // Container - pin to top of the screen with two scroll states
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: containerRef.current,
        start: "top top",
        end: `+=${200}%`, // Two scroll states
        scrub: true,
        pin: true,
        anticipatePin: 1,
        snap: {
          snapTo: [0, 1], // Only snap to start and end
          duration: 0.5,
          ease: "power2.out",
        },
      },
    });

    // -----------------------------------------------------------------------
    // State 1: Section 1 fadeout (first 50% of scroll)
    tl.addLabel("start")
      .to(section1Ref.current, {
        opacity: 0,
        ease: "none",
        duration: 1,
      })
      .to(
        imgSection1Ref.current,
        {
          filter: "blur(10px)",
          ease: "none",
          duration: 1,
        },
        "<",
      )
      .to(
        title1Ref.current,
        {
          y: -50,
          opacity: 0,
          ease: "none",
          duration: 1,
          force3D: true,
          "-webkit-transform-style": "preserve-3d",
          "-webkit-backface-visibility": "hidden",
        },
        "<",
      );

    // -----------------------------------------------------------------------
    // State 2: Complete content reveal - headline and buttons together (second 50% of scroll)
    tl.addLabel("final")
      .to(section4Ref.current, {
        opacity: 1,
        ease: "none",
        duration: 1,
      })
      .to(
        section4ContentRef.current,
        {
          opacity: 1,
          ease: "none",
          duration: 1,
          onStart: () => {
            // Apply word-by-word animation to section 4 title
            createWordAnimation(section4TitleRef.current, 0.1, 0.08);
          },
        },
        "<",
      )
      .from(
        section4ButtonsRef.current,
        {
          y: "50px",
          opacity: 0,
          ease: "power2.out",
          duration: 1,
        },
        "<+=0.2", // Small delay to let headline start first
      );

    // Set initial positions
    gsap.set(section4ContentRef.current, {
      opacity: 0,
    });

    gsap.set(section4ButtonsRef.current, {
      y: "50px",
      opacity: 0,
    });

    // Cleanup function - store timeline reference for cleanup
    return () => {
      if (tl.scrollTrigger) {
        tl.scrollTrigger.kill();
      }
      tl.kill();
    };
  }, [introText, section4_title]);

  // Validation after all hooks are declared
  if (
    !introText ||
    !section1Image ||
    !section4_title ||
    !section4Image ||
    !section4Graphic
  ) {
    console.error("HomepageHero: Missing required props");
    return <div>Loading...</div>;
  }

  return (
    <div ref={containerRef} className={cx("heroContainer")}>
      {/* Section #1 */}
      <section ref={section1Ref} className={cx("section", "section1")}>
        {/* Background Image */}
        <div ref={imgSection1Ref} className={cx("section1__backgroundImage")}>
          <Image
            src={heroImage1.url}
            alt={heroImage1.alt ?? ""}
            fill
            sizes="100vw"
            aria-hidden
            priority
          />

          {/* Pixelated grid overlay */}
          <div className={cx("section1__gridOverlay")} style={gridStyle}>
            {gridCells}
          </div>

          {/* Pixelated black grid */}
          <div className={cx("section1__gridOverlay")} style={gridStyle}>
            {gridCellsBlack}
          </div>

          {/* Text */}
          <div className={cx("section1__textContainer")}>
            <h1 ref={title1Ref} className={cx("section1__heading")}>
              {introText}
            </h1>
          </div>
        </div>
      </section>

      {/* Section #4 */}
      <section ref={section4Ref} className={cx("section", "section4")}>
        {/* Background Image */}
        <div className={cx("section4__backgroundImage")}>
          <Image
            src={heroImage4.url}
            alt={heroImage4.alt ?? ""}
            fill
            sizes="100vw"
            aria-hidden
            priority={false}
          />
        </div>

        {/* Rive or Graphic Fallback */}
        <div ref={section4GraphicRef} className={cx("section4__graphic")}>
          {/* Rive Animations - only show if valid Rive data is provided */}
          {section4Rive?.file && section4Rive?.animation ? (
            <div className={cx("section4__rive")}>
              {/* Mobile Rive - only show on mobile if available */}
              {section4MobileRive?.file && section4MobileRive?.animation && (
                <div className={cx("section4__rive-mobile")}>
                  <RivePlayer
                    ref={riveMobileSection4Ref}
                    src={getRiveFileUrl(section4MobileRive.file)}
                    autoplay={false}
                    animations={section4MobileRive.animation}
                  />
                </div>
              )}
              {/* Desktop Rive */}
              <div className={cx("section4__rive-desktop")}>
                <RivePlayer
                  ref={riveSection4Ref}
                  src={getRiveFileUrl(section4Rive.file)}
                  autoplay={false}
                  animations={section4Rive.animation}
                />
              </div>
            </div>
          ) : (
            /* Fallback Graphic - show when no valid Rive is provided */
            <Image
              className={cx("section4__graphicImage")}
              src={heroGraphic.url}
              alt={heroGraphic.alt ?? "NLX Graphic"}
              width={1300}
              height={698}
            />
          )}
        </div>

        {/* Content */}
        <div ref={section4ContentRef} className={cx("section4__content")}>
          <h2 ref={section4TitleRef} className={cx("section4__title")}>
            {section4_title}
          </h2>
          <div ref={section4ButtonsRef} className={cx("section4__buttons")}>
            <Button href={section4_btn1_url}>{section4_btn1_label}</Button>
            <Button variant="secondary-light" href={section4_btn2_url}>
              {section4_btn2_label}
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
